
import { Trip } from "@/types/trip";
import { TimelineEntry } from "./TimelineEntry";
import { TimelineDateIndicator } from "./TimelineDateIndicator";
import { useIsMobile } from "@/hooks/use-mobile";
import { parseISO, isAfter, isBefore, compareAsc } from "date-fns";
import { DesktopTripList } from "./DesktopTripList";
import { useAnalytics } from "@/hooks/use-analytics";
import { useEffect } from "react";

interface TripListProps {
  futureTrips: Trip[];
  pastTrips: Trip[];
  isTimelineVisible: boolean;
  handleRefreshTrips?: () => void;
}

export const TripList = ({
  futureTrips,
  pastTrips,
  isTimelineVisible,
  handleRefreshTrips
}: TripListProps) => {
  const isMobile = useIsMobile();
  const { trackTimelineAction, trackIndexAction } = useAnalytics();
  
  // Track initial view of the trip list
  useEffect(() => {
    if (futureTrips.length > 0 || pastTrips.length > 0) {
      trackIndexAction('scroll_timeline', undefined, 
        `Viewing ${futureTrips.length} future and ${pastTrips.length} past trips`);
    }
  }, [futureTrips.length, pastTrips.length, trackIndexAction]);
  
  // Find the first ongoing trip (a trip with at least one segment that's currently ongoing)
  const findOngoingTrip = (trips: Trip[]): Trip | null => {
    try {
      const now = new Date();
      return trips.find(trip => {
        try {
          return trip.elements.some(element => {
            const startDate = parseISO(element.startDate || "1970");
            const endDate = parseISO(element.endDate || "1970");
            return !isAfter(startDate, now) && !isBefore(endDate, now);
          });
        } catch (error) {
          console.error('Error finding ongoing trip:', error);
          return false;
        }
      }) || null;
    } catch (error) {
      console.error('Error in findOngoingTrip:', error);
      return null;
    }
  };

  // Find trip to focus on (first ongoing trip, next upcoming trip, or most recent past trip)
  const findFocusedTrip = (): Trip | null => {
    try {
      // First check for ongoing trips
      const ongoingTrip = findOngoingTrip(futureTrips);
      if (ongoingTrip) return ongoingTrip;
      
      // If no ongoing trip, focus on the next upcoming trip (first in futureTrips)
      if (futureTrips.length > 0) return futureTrips[0];
      
      // If no future trips, focus on the most recent past trip (last in sortedPastTrips)
      if (pastTrips.length > 0) {
        // Sort past trips from oldest to newest
        const sortedPastTrips = [...pastTrips].sort((a, b) => {
          try {
            if (!a.elements?.length || !b.elements?.length) {
              return 0;
            }
            // Sort by the end date of the last trip element
            const aDate = a.elements.length > 0 ? parseISO(a.elements[a.elements.length - 1].endDate || "1970") : new Date(0);
            const bDate = b.elements.length > 0 ? parseISO(b.elements[b.elements.length - 1].endDate || "1970") : new Date(0);
            return compareAsc(aDate, bDate); // Oldest first (ascending)
          } catch (error) {
            console.error('Error sorting past trips for focus:', error);
            return 0;
          }
        });
        
        // Return the most recent past trip (last one in the sorted array)
        return sortedPastTrips[sortedPastTrips.length - 1];
      }
      
      // If no trips at all, return null
      return null;
    } catch (error) {
      console.error('Error finding focused trip:', error);
      return null;
    }
  };

  // Make sure pastTrips are sorted from oldest to newest (left to right)
  const sortedPastTrips = [...pastTrips].sort((a, b) => {
    try {
      if (!a.elements?.length || !b.elements?.length) {
        return 0; // If any trip has no elements, consider them equal
      }
      // Sort by the end date of the last trip element
      const aDate = a.elements.length > 0 ? parseISO(a.elements[a.elements.length - 1].endDate || "1970") : new Date(0);
      const bDate = b.elements.length > 0 ? parseISO(b.elements[b.elements.length - 1].endDate || "1970") : new Date(0);
      return compareAsc(aDate, bDate); // Oldest first (ascending)
    } catch (error) {
      console.error('Error sorting past trips:', error);
      return 0;
    }
  });

  // Make sure futureTrips are sorted from nearest to furthest in the future (left to right)
  const sortedFutureTrips = [...futureTrips].sort((a, b) => {
    try {
      if (!a.elements?.length || !b.elements?.length) {
        return 0; // If any trip has no elements, consider them equal
      }
      // Sort by the start date of the first trip element
      const aDate = a.elements.length > 0 ? parseISO(a.elements[0].startDate || "1970") : new Date();
      const bDate = b.elements.length > 0 ? parseISO(b.elements[0].startDate || "1970") : new Date();
      return compareAsc(aDate, bDate); // Nearest future first (ascending)
    } catch (error) {
      console.error('Error sorting future trips:', error);
      return 0;
    }
  });

  const focusedTrip = findFocusedTrip();
  
  // Track the focused trip
  useEffect(() => {
    if (focusedTrip) {
      trackTimelineAction(
        'view_details', 
        'timeline', 
        focusedTrip.id, 
        focusedTrip.destination
      );
    }
  }, [focusedTrip, trackTimelineAction]);


  // For desktop view, use the grid layout component
  if (!isMobile) {
    return <DesktopTripList futureTrips={sortedFutureTrips} pastTrips={sortedPastTrips} handleRefreshTrips={handleRefreshTrips} />;
  }
  // For mobile view, render the timeline entries
  const renderTrips = (trips: Trip[]) => {
    try {
      return trips.map((trip) => {
        try {
          const isFocused = focusedTrip && trip.id === focusedTrip.id;
          return <TimelineEntry 
            key={trip.id} 
            trip={trip} 
            index={trips.indexOf(trip)} 
            isTimelineVisible={isTimelineVisible} 
            isFocused={isFocused} 
            handleRefreshTrips={handleRefreshTrips}
          />;
        } catch (error) {
          console.error('Error rendering trip:', trip.id, error);
          return null;
        }
      });
    } catch (error) {
      console.error('Error rendering trips:', error);
      return null;
    }
  };

  // For mobile view, keep the existing timeline
  return (
    <div style={{ zIndex: 0 }} className="px-4">
      <div className="flex flex-row items-center gap-6 h-full">
        {renderTrips(sortedPastTrips)}
        <TimelineDateIndicator />
        {renderTrips(sortedFutureTrips)}
      </div>
    </div>
  );
};
